import '@qrkey/ui/styles.css';
import './styles.css';
import { ToastProvider } from '@qrkey/ui';
import { QueryClientProvider } from '@tanstack/react-query';
import { RouterProvider } from '@tanstack/react-router';
import { StrictMode, useEffect } from 'react';
import { createRoot } from 'react-dom/client';
import { setSentryCompany, startBrowserSentry } from '@qrkey/observability/browser';
import { useSession } from './app/api.js';
import { queryClient } from './app/query.js';
import { router } from './app/router.js';
import { StaffThemeProvider } from './app/StaffThemeProvider.js';
import { startOffline } from './offline/index.js';
import { UpdatePrompt } from './pwa/UpdatePrompt.js';
import { startServiceWorker } from './pwa/update.js';

function App() {
  const session = useSession();
  const companyId = session?.session.companyId;
  // Error reports carry the company, never the person.
  useEffect(() => setSentryCompany(companyId), [companyId]);
  // Heartbeat and background sync of the offline store (M4).
  useEffect(() => startOffline(), []);
  // App-shell cache for offline starts; a new build waits until the person taps Update.
  useEffect(() => {
    startServiceWorker();
  }, []);
  // Each person's theme on this device; the outlet's `ui.theme` is the default until they
  // pick their own, and KDS terminals default to the dark theme.
  return (
    <StaffThemeProvider>
      <ToastProvider>
        <RouterProvider router={router} />
        <UpdatePrompt />
      </ToastProvider>
    </StaffThemeProvider>
  );
}

// Error reports only when the build has VITE_SENTRY_DSN; the SDK is then loaded on its
// own (never part of the first load). Scrubbed before sending.
void startBrowserSentry(
  {
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT,
    release: import.meta.env.VITE_SENTRY_RELEASE,
    app: 'staff',
    mode: import.meta.env.MODE,
  },
  () => import('@sentry/react'),
);

const root = document.getElementById('root');
if (root) {
  createRoot(root).render(
    <StrictMode>
      <QueryClientProvider client={queryClient}>
        <App />
      </QueryClientProvider>
    </StrictMode>,
  );
}
